import _ from 'lodash';
import moment, { type Moment } from 'moment';

/**
 * Deinterleaves a flat ECG sample buffer into one array per channel. Walks the buffer in strides of totalChannel and pushes each sample onto its channel, bailing out when the buffer is missing.
 *
 * @param totalChannel - Number of interleaved channels in the buffer.
 * @param binaryData - Interleaved ECG samples.
 * @returns One sample array per channel, or undefined when binaryData was absent.
 */
export const convertBinaryToECGData = (totalChannel: number, binaryData: Int16Array | number[]) => {
  if (!binaryData) {
    return undefined;
  }
  const ecgData: number[][] = new Array(totalChannel).fill(null).map((): number[] => []);
  const binaryDataLength = binaryData.length;
  for (let i = 0; i < binaryDataLength; i += totalChannel) {
    for (let j = 0; j < totalChannel; j += 1) {
      ecgData[j].push(binaryData[i + j]);
    }
  }
  return ecgData;
};

/**
 * Builds the placeholder sine wave drawn across gaps in an ECG recording. Generates one second of samples scaled to three quarters of half the ADC gain and offset by 1.25 gain either side of the half period, repeats it for the whole seconds in the gap and appends a proportional slice for the remainder.
 *
 * @param diffMilliseconds - Length of the gap to fill, in milliseconds.
 * @param samplingFrequency - Sampling rate, also the sample count of one wave period.
 * @param adcGain - ADC gain the wave amplitude and offset are scaled to.
 * @returns The sine wave samples covering the gap.
 */
export const getSineWaveDataArray = (
  diffMilliseconds: number,
  samplingFrequency: number,
  adcGain: number,
): number[] => {
  const halfSamplingFrequency = samplingFrequency / 2;
  const sineWaveCount = diffMilliseconds / 1000;

  // *: Create positive & negative sinewave with translating y-offset by 0.75 * adcGain (1.5 square)
  const sineWaveDataArray = _.map(_.range(samplingFrequency), (x, i) => {
    const sample = Math.sin(x * ((2 * Math.PI) / samplingFrequency)) * (0.75 * (adcGain / 2));
    return i <= halfSamplingFrequency
      ? Math.round(sample - 1.25 * adcGain)
      : Math.round(sample + 1.25 * adcGain);
  });
  let resultSineWave: number[] = [];
  const wholeSineWaveCount = Math.floor(sineWaveCount);
  const fractionalSineWaveCount = sineWaveCount - wholeSineWaveCount;
  for (let k = 0; k < wholeSineWaveCount; k += 1) {
    resultSineWave = resultSineWave.concat(sineWaveDataArray);
  }
  if (fractionalSineWaveCount) {
    resultSineWave = resultSineWave.concat(
      sineWaveDataArray.slice(0, Math.round(fractionalSineWaveCount * sineWaveDataArray.length)),
    );
  }
  return resultSineWave;
};

/**
 * Clips the recorded data ranges to a requested window and pins the window edges. Keeps a range only where its clamped start still precedes its clamped stop, then brackets the result with zero-width ranges at start and stop.
 *
 * @param ranges - Recorded epoch ranges as start and stop pairs.
 * @param start - Window start epoch.
 * @param stop - Window stop epoch.
 * @returns The clipped ranges bracketed by zero-width start and stop markers.
 */
export const combineRanges = (ranges: number[][], start: number, stop: number): number[][] => {
  const result: number[][] = [];
  for (const subarray of ranges) {
    let [subStart, subStop] = subarray;
    // Adjust start and stop values based on the provided range
    subStart = Math.max(subStart, start);
    subStop = Math.min(subStop, stop);
    // Include the adjusted range in the result
    if (subStart <= subStop) {
      result.push([subStart, subStop]);
    }
  }
  // Include the start and stop values as separate ranges
  if (start <= stop) {
    result.unshift([start, start]);
    result.push([stop, stop]);
  }
  return result;
};

/**
 * Splices placeholder sine waves into downloaded ECG so gaps in the recording keep their real duration. Returns the input untouched when it already spans dataLength, otherwise clips the ranges to the window and walks them, concatenating a generated wave for each gap and the matching slice of real samples for each covered span.
 *
 * @param selectedEcgData - Downloaded samples, one array per channel.
 * @param ranges - Recorded ranges covering the window, each with a start and stop.
 * @param startMoment - Window start.
 * @param stopMoment - Window stop.
 * @param ecgDataMap - ECG map supplying samplingFrequency and gain.
 * @param dataLength - Expected sample count per channel; a match short-circuits the work.
 * @returns The per-channel samples with every gap filled by a sine wave.
 */
export const addSineWaveToEcgData = (
  selectedEcgData: number[][],
  ranges: any[],
  startMoment: number | string | Moment,
  stopMoment: number | string | Moment,
  ecgDataMap: any,
  dataLength = 75000,
): number[][] => {
  if (selectedEcgData[0].length === dataLength) {
    return selectedEcgData;
  }
  const { samplingFrequency, gain } = ecgDataMap;
  const ecgData: number[][] = _.map(selectedEcgData, (): number[] => []);
  const rangesEpochs = _.map(ranges, (x) => [moment(x.start).valueOf(), moment(x.stop).valueOf()]);
  const startEpoch = moment(startMoment).valueOf();
  const stopEpoch = moment(stopMoment).valueOf();
  const rangesData = combineRanges(rangesEpochs, startEpoch, stopEpoch);
  let dataIndex = 0;
  _.forEach(rangesData, (range, i) => {
    if (i > 0) {
      const currStart = range[0];
      const currStop = range[1];
      const prevStop = rangesData[i - 1][1];
      if (currStart - prevStop > 0) {
        const sineWave = getSineWaveDataArray(currStart - prevStop, samplingFrequency, gain);
        for (let j = 0; j < ecgData.length; j += 1) {
          ecgData[j] = ecgData[j].concat(sineWave);
        }
      }
      if (currStop - currStart > 0) {
        const diffMs = (currStop - currStart) / 1000;
        const dataLength = Math.round(diffMs * samplingFrequency);
        for (let j = 0; j < ecgData.length; j += 1) {
          ecgData[j] = ecgData[j].concat(
            selectedEcgData[j].slice(dataIndex, dataIndex + dataLength),
          );
        }
        dataIndex += dataLength;
      }
    }
  });
  return ecgData;
};
